import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    ListChecks,
    ArrowLeft,
    ArrowRight,
    Loader2,
    Trophy,
    Star as StarIcon,
    AlertTriangle,
    RefreshCw,
} from "lucide-react";
import api from "../../lib/api";
import useSeo from "../../hooks/useSeo";

export default function StarsChecklist() {
    const { i18n } = useTranslation();
    const lang = (i18n.language || "tr").split("-")[0];
    const [stars, setStars] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeStar, setActiveStar] = useState(3);
    const [answers, setAnswers] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    useSeo({
        title: "Kaç Yıldızı Hak Ediyorsunuz?",
        description: "İnteraktif kontrol listesi — tesisinizin kriterlerini işaretleyin, hangi yıldız seviyesini hak ettiğinizi öğrenin.",
        path: "/yildiz-standartlari/kontrol",
    });

    useEffect(() => {
        (async () => {
            try {
                const promises = [1, 2, 3, 4, 5].map((s) => api.get(`/stars/${s}`).then((r) => r.data));
                const results = await Promise.all(promises);
                setStars(results);
            } catch (_) { /* ignore */ }
            finally {
                setLoading(false);
            }
        })();
    }, []);

    const tr = (obj) => (obj && (obj[lang] || obj.tr)) || "";

    const toggleAnswer = (key) => {
        setAnswers((p) => ({ ...p, [key]: !p[key] }));
    };

    const submit = async () => {
        setSubmitting(true);
        try {
            const { data } = await api.post("/stars/check", { answers });
            setResult(data);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (_) { /* ignore */ }
        finally {
            setSubmitting(false);
        }
    };

    const reset = () => {
        setAnswers({});
        setResult(null);
        setActiveStar(3);
    };

    const currentStarData = stars.find((s) => s.star_level === activeStar);

    const counts = useMemo(() => {
        let total = 0;
        let answered = 0;
        stars.forEach((s) => {
            (s.categories || []).forEach((cat, ci) => {
                (cat.requirements || []).forEach((req) => {
                    total++;
                    const key = `${s.star_level}::${ci}::${req.sort_order}`;
                    if (answers[key]) answered++;
                });
            });
        });
        return { total, answered };
    }, [answers, stars]);

    return (
        <div className="bg-light-bg min-h-[80vh]" data-testid="stars-checklist-page">
            <section className="bg-dark-navy text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#08111d] via-[#0c2438] to-[#123b52]" />
                <div className="relative max-w-5xl mx-auto px-6 py-12 lg:py-16">
                    <Link to="/yildiz-standartlari" className="text-xs uppercase tracking-[0.2em] text-teal font-semibold inline-flex items-center gap-1 hover:text-white">
                        <ArrowLeft className="w-3.5 h-3.5" /> Yıldız Standartları
                    </Link>
                    <h1 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight inline-flex items-center gap-3">
                        <ListChecks className="w-8 h-8 text-gold" /> Kaç Yıldızı Hak Ediyorsunuz?
                    </h1>
                    <p className="mt-3 text-white/75 max-w-2xl">
                        Aşağıdaki tüm kriterleri tesisinize göre işaretleyin. Otel Pro otomatik olarak hak ettiğiniz en yüksek yıldız seviyesini hesaplar.
                    </p>
                </div>
            </section>

            {result && (
                <section className="max-w-5xl mx-auto px-6 -mt-8 relative z-10" data-testid="stars-checklist-result">
                    <div className="bg-white rounded-lg shadow-2xl border border-slate-100 p-7">
                        <div className="flex items-start gap-5 flex-wrap">
                            <div className={`w-24 h-24 rounded-md flex items-center justify-center shrink-0 ${result.qualifying_star > 0 ? "bg-gold/15 text-gold-700" : "bg-data-red/10 text-data-red"}`}>
                                <Trophy className="w-12 h-12" />
                            </div>
                            <div className="flex-1 min-w-[260px]">
                                <span className="text-xs uppercase tracking-[0.2em] text-teal font-semibold">Sonuç</span>
                                <h2 className="text-2xl sm:text-3xl font-bold text-dark-navy mt-1">
                                    {result.qualifying_star > 0 ? (
                                        <>
                                            <span className="font-data text-gold mr-2">{Array.from({ length: result.qualifying_star }).map(() => "★").join("")}</span>
                                            {result.qualifying_star} Yıldız hak ediyorsunuz
                                        </>
                                    ) : (
                                        "Henüz uygun yıldız seviyesi bulunamadı"
                                    )}
                                </h2>
                                <p className="text-sm text-deep-blue/70 mt-1">
                                    Tüm zorunlu kriterleri karşıladığınız ve opsiyonel kriterlerin en az %60'ını sağladığınız en yüksek seviye.
                                </p>
                            </div>
                            <button onClick={reset} className="inline-flex items-center gap-2 text-sm font-semibold text-deep-blue hover:text-dark-navy" data-testid="stars-checklist-reset">
                                <RefreshCw className="w-4 h-4" /> Sıfırla
                            </button>
                        </div>

                        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
                            {result.breakdown.map((b) => (
                                <div key={b.star_level} className={`rounded-md p-4 border ${b.qualifies ? "border-data-green/40 bg-data-green/5" : "border-slate-200 bg-light-bg"}`}>
                                    <div className="font-data text-xl text-gold">{Array.from({ length: b.star_level }).map(() => "★").join("")}</div>
                                    <div className="mt-1 text-[10px] uppercase tracking-wider text-deep-blue/70 font-semibold">{b.star_level} Yıldız</div>
                                    <div className="mt-2 text-xs text-deep-blue/85">
                                        Zorunlu: {b.met_mandatory}/{b.total_mandatory} ({b.mandatory_pct.toFixed(0)}%)
                                    </div>
                                    <div className="text-xs text-deep-blue/85">
                                        Opsiyonel: {b.met_optional}/{b.total_optional} ({b.optional_pct.toFixed(0)}%)
                                    </div>
                                    <div className={`mt-2 inline-block px-2 py-0.5 rounded text-[10px] font-bold ${b.qualifies ? "bg-data-green text-white" : "bg-slate-200 text-slate-600"}`}>
                                        {b.qualifies ? "UYGUN" : "EKSİK"}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {result.qualifying_star < 5 && (
                            <div className="mt-5 p-4 rounded-md bg-gold/10 border-l-4 border-gold">
                                <div className="text-xs uppercase tracking-wider font-semibold text-gold-700 inline-flex items-center gap-2">
                                    <AlertTriangle className="w-4 h-4" /> Bir üst seviyeye yükselmek için
                                </div>
                                <ul className="mt-2 list-disc pl-5 text-sm text-deep-blue/85 space-y-1">
                                    {(result.breakdown.find((b) => b.star_level === Math.min(5, result.qualifying_star + 1))?.missing_mandatory || []).map((m, i) => (
                                        <li key={i}><strong>{tr(m.category)}:</strong> {tr(m.text)}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </section>
            )}

            <section className="max-w-5xl mx-auto px-6 py-12">
                <div className="bg-white rounded-lg border border-slate-100 shadow-md p-6">
                    <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
                        <div>
                            <h2 className="text-base font-bold text-dark-navy">Kriter Listesi</h2>
                            <p className="text-xs text-deep-blue/70 mt-1">İşaretlenen: <strong className="font-data">{counts.answered}</strong> / {counts.total}</p>
                        </div>
                        <div className="flex gap-1 flex-wrap">
                            {[1, 2, 3, 4, 5].map((s) => (
                                <button
                                    key={s}
                                    onClick={() => setActiveStar(s)}
                                    data-testid={`stars-checklist-tab-${s}`}
                                    className={`px-3 py-2 rounded-md text-sm font-semibold transition ${activeStar === s ? "bg-dark-navy text-white" : "bg-light-bg text-deep-blue hover:bg-slate-200"}`}
                                >
                                    <span className="font-data text-gold mr-1">{Array.from({ length: s }).map(() => "★").join("")}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {loading ? (
                        <div className="text-center text-deep-blue/60 text-sm py-12 inline-flex items-center justify-center gap-2 w-full">
                            <Loader2 className="w-5 h-5 animate-spin" /> Yükleniyor...
                        </div>
                    ) : currentStarData ? (
                        <div className="space-y-4" data-testid={`stars-checklist-categories-${activeStar}`}>
                            {(currentStarData.categories || []).map((cat, ci) => (
                                <div key={ci}>
                                    <h3 className="text-sm font-bold text-dark-navy uppercase tracking-wider">{tr(cat.name)}</h3>
                                    <div className="mt-2 space-y-2">
                                        {(cat.requirements || []).map((req, ri) => {
                                            const key = `${currentStarData.star_level}::${ci}::${req.sort_order}`;
                                            const checked = !!answers[key];
                                            return (
                                                <label
                                                    key={ri}
                                                    className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition ${checked ? "border-teal bg-teal/5" : "border-slate-200 bg-white hover:bg-light-bg"}`}
                                                    data-testid={`stars-checklist-item-${activeStar}-${ci}-${req.sort_order}`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={checked}
                                                        onChange={() => toggleAnswer(key)}
                                                        className="mt-1 w-4 h-4 accent-teal"
                                                    />
                                                    <div className="flex-1">
                                                        <p className="text-sm text-deep-blue/85">{tr(req.text)}</p>
                                                        <span className={`mt-1 inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${req.is_mandatory ? "bg-data-green/10 text-data-green" : "bg-slate-200 text-slate-600"}`}>
                                                            {req.is_mandatory ? "Zorunlu" : "Opsiyonel"}
                                                        </span>
                                                    </div>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : null}

                    <div className="mt-7 flex items-center gap-3 flex-wrap">
                        <button
                            onClick={submit}
                            disabled={submitting || counts.answered === 0}
                            className="bg-gold hover:bg-gold-700 text-dark-navy text-sm font-bold px-6 py-3 rounded-md inline-flex items-center gap-2 disabled:opacity-60 shadow-md"
                            data-testid="stars-checklist-submit"
                        >
                            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <StarIcon className="w-4 h-4" />}
                            Sonucu Hesapla <ArrowRight className="w-4 h-4" />
                        </button>
                        {counts.answered > 0 && (
                            <button onClick={reset} className="text-sm font-semibold text-deep-blue hover:text-dark-navy">
                                Sıfırla
                            </button>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}
